import React from 'react';
import {
  BoxArrowRight,
  CreditCard,
  Gear,
  Link45deg,
  Lock,
  Person,
  PersonX,
  ShieldLock,
} from 'react-bootstrap-icons';
import './ProfileMenuDropdown.css';

export type ProfileMenuItemKey =
  | 'settings'
  | 'billing'
  | 'profile'
  | 'connections'
  | 'security'
  | 'privacy'
  | 'delete'
  | 'logout';

export interface ProfileMenuItem {
  key: ProfileMenuItemKey;
  label: string;
  icon: React.ReactNode;
  badge?: string;
  /** When false, item is wired to a route or action (no "Soon" label). */
  soon?: boolean;
  danger?: boolean;
}

interface ProfileMenuDropdownProps {
  show: boolean;
  displayName?: string;
  onClose: () => void;
  onSelect: (key: ProfileMenuItemKey) => void;
}

const DEFAULT_ITEMS: ProfileMenuItem[] = [
  { key: 'settings', label: 'Account settings', icon: <Gear size={17} /> },
  {
    key: 'billing',
    label: 'Subscription & billing',
    icon: <CreditCard size={17} />,
    badge: 'Outside beta',
    soon: true,
  },
  {
    key: 'profile',
    label: 'Profile customization',
    icon: <Person size={17} />,
  },
  {
    key: 'connections',
    label: 'Connected accounts',
    icon: <Link45deg size={17} />,
  },
  {
    key: 'security',
    label: 'Security',
    icon: <Lock size={17} />,
  },
  {
    key: 'privacy',
    label: 'Privacy',
    icon: <ShieldLock size={17} />,
  },
  {
    key: 'delete',
    label: 'Account deletion & deactivation',
    icon: <PersonX size={17} />,
    danger: true,
  },
  {
    key: 'logout',
    label: 'Logout',
    icon: <BoxArrowRight size={17} />,
    danger: true,
  },
];

const ProfileMenuDropdown: React.FC<ProfileMenuDropdownProps> = ({
  show,
  displayName,
  onClose,
  onSelect,
}) => {
  if (!show) return null;

  return (
    <>
      <button
        type="button"
        className="profile-menu-backdrop"
        aria-label="Close menu"
        onClick={onClose}
      />
      <div className="profile-menu-panel" role="dialog" aria-label="Account menu">
        <div className="profile-menu-header">
          <div className="profile-menu-header-left">
            <span className="profile-menu-icon-wrap" aria-hidden>
              <Person size={18} />
            </span>
            <div>
              <h6 className="profile-menu-title">Account</h6>
              {displayName ? (
                <p className="profile-menu-sub">{displayName}</p>
              ) : (
                <p className="profile-menu-sub">Settings & account actions</p>
              )}
            </div>
          </div>
          <button type="button" className="profile-menu-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <ul className="profile-menu-list">
          {DEFAULT_ITEMS.map((item) => (
            <li key={item.key}>
              <button
                type="button"
                className={`profile-menu-item ${
                  item.danger ? 'profile-menu-item--danger' : ''
                } ${item.soon ? 'profile-menu-item--soon' : ''}`}
                onClick={() => onSelect(item.key)}
              >
                <span className="profile-menu-item-icon" aria-hidden>
                  {item.icon}
                </span>
                <span className="profile-menu-item-label">{item.label}</span>
                {item.badge ? <span className="profile-menu-chip">{item.badge}</span> : null}
                {item.soon ? <span className="profile-menu-soon">Soon</span> : null}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
};

export default ProfileMenuDropdown;
